import { useEffect, useMemo } from "react";
import { UserCheck, Clock, Umbrella, FileText } from "lucide-react";
import { YesNoInput, SelectInput, TextInput, NumberInput, CheckboxInput, MultiSelectInput } from "../components/fields";
import { Alert } from "../components/Toast";
import { useFormStore } from "../store/formStore";
import type { useStepValidation } from "../hooks";

interface StepEligibilityProps {
  validation: ReturnType<typeof useStepValidation>;
}

export function StepEligibility({ validation }: StepEligibilityProps) {
  const excludeClass = useFormStore((s) => s.data.excludeClass);
  const excludedClasses = useFormStore((s) => s.data.excludedClasses);
  const excludedClassesOptionsMeta = useFormStore((s) => s.meta.excludedClassesOptions);
  const waitingPeriodOptionsMeta = useFormStore((s) => s.meta.waitingPeriodOptions);
  const waitingPeriod = useFormStore((s) => s.data.waitingPeriod);
  const waiveWaitingPeriod = useFormStore((s) => s.data.waiveWaitingPeriod);
  const offerCOBRAVoluntary = useFormStore((s) => s.data.offerCOBRAVoluntary);
  const wantOptimylSection125 = useFormStore((s) => s.data.wantOptimylSection125);
  const avgW2Employees20Weeks = useFormStore((s) => s.data.avgW2Employees20Weeks);
  const setField = useFormStore((s) => s.setField);

  const waitingPeriodSelectOptions = useMemo(() => {
    const opts = [...waitingPeriodOptionsMeta];
    if (waitingPeriod && !opts.includes(waitingPeriod)) opts.push(waitingPeriod);
    return opts.map((v) => ({ value: v, label: v }));
  }, [waitingPeriodOptionsMeta, waitingPeriod]);

  const excludedClassesSelectOptions = useMemo(() => {
    const opts = [...excludedClassesOptionsMeta];
    for (const v of excludedClasses) if (!opts.includes(v)) opts.push(v);
    return opts;
  }, [excludedClassesOptionsMeta, excludedClasses]);

  const isSmallGroup = (avgW2Employees20Weeks ?? 0) < 20;
  const showCOBRAFollowup = !isSmallGroup || offerCOBRAVoluntary === "Yes";

  // Clear fields that are hidden due to a controlling value changing, so stale
  // values don't persist. These run in the store (not just the save snapshot)
  // so isDirty becomes true and the next save actually sends the nulls to SF.
  useEffect(() => {
    if (!isSmallGroup && useFormStore.getState().data.offerCOBRAVoluntary !== null) {
      setField("offerCOBRAVoluntary", "");
    }
  }, [isSmallGroup, setField]);

  return (
    <div>
      <h1 className="text-2xl font-bold text-brand-navy mb-1">Eligibility & Elections</h1>
      <p className="text-gray-500 mb-6">Employee eligibility rules and elections</p>

      {/* Eligibility Rules */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-navy mb-4 flex items-center gap-2">
          <UserCheck size={20} className="text-gray-500" />
          Eligibility Rules
        </h2>
        <YesNoInput
          field="excludeClass"
          label="Will you exclude a class of employees from this plan?"
          validation={validation}
          required
          onChange={(v) => {
            if (v === "No") {
              setField("excludedClasses", []);
              setField("excludedClassesOther", "");
              validation.clearErrors(["excludedClasses", "excludedClassesOther"]);
            }
          }}
        />
        {excludeClass === "Yes" && (
          <div className="mt-4 space-y-4 section-reveal">
            <MultiSelectInput
              field="excludedClasses"
              label="Excluded employee class"
              options={excludedClassesSelectOptions.map((lbl) => ({ value: lbl, label: lbl }))}
              validation={validation}
              required
              onChange={(newVal) => {
                if (!newVal.includes("Other")) {
                  setField("excludedClassesOther", "");
                  validation.clearErrors(["excludedClassesOther"]);
                }
              }}
            />
            {excludedClasses.includes("Other") && (
              <TextInput
                field="excludedClassesOther"
                label="Specify excluded class"
                validation={validation}
                required
                maxLength={80}
              />
            )}
          </div>
        )}

        <hr className="my-5 border-gray-200" />

        <YesNoInput
          field="allowDomesticPartners"
          label="Will you allow Domestic Partners to be eligible?"
          validation={validation}
          required
        />

        <hr className="my-5 border-gray-200" />

        <NumberInput
          field="minHoursPerWeek"
          label="Minimum hours per week for eligibility"
          validation={validation}
          required
          min={20}
          max={40}
          maxLength={2}
          hint="Between 20 and 40. Default is 30 hours."
        />
      </div>

      {/* Waiting Period */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-navy mb-4 flex items-center gap-2">
          <Clock size={20} className="text-gray-500" />
          Waiting Period
        </h2>
        <SelectInput
          field="waitingPeriod"
          label="Waiting period for new eligible employees"
          options={waitingPeriodSelectOptions}
          placeholder="Select waiting period..."
          validation={validation}
          required
        />

        <hr className="my-5 border-gray-200" />

        <YesNoInput
          field="waiveWaitingPeriod"
          label="I would like to waive the waiting period for current enrolling employees (even those who have not met the full waiting period)."
          validation={validation}
          required
          onChange={(v) => {
            if (v !== "No") {
              setField("employeesInWaitingPeriod", "");
              validation.clearErrors(["employeesInWaitingPeriod"]);
            }
          }}
        />
        <Alert
          type="info"
          showIcon={false}
          message="Please note this option is only available to employers with 25 or fewer medical plan enrollees."
          className="mt-3"
        />
        {waiveWaitingPeriod === "No" && (
          <div className="mt-4 section-reveal">
            <hr className="mb-5 border-gray-200" />
            <YesNoInput
              field="employeesInWaitingPeriod"
              label="Any employees currently in their waiting period?"
              validation={validation}
              required
            />
          </div>
        )}
      </div>

      {/* COBRA */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-navy mb-4 flex items-center gap-2">
          <Umbrella size={20} className="text-gray-500" />
          COBRA
        </h2>
        {isSmallGroup ? (
          <>
            <Alert
              type="info"
              showIcon={false}
              message="COBRA continuation is not required unless you have at least 20 or more employees."
              className="mb-4"
            />
            <YesNoInput
              field="offerCOBRAVoluntary"
              label="Would you like to voluntarily offer COBRA continuation coverage?"
              validation={validation}
              required
              onChange={(v) => {
                if (v === "No") {
                  setField("employeesOnCOBRA", "");
                  setField("useOptimylCOBRA", "");
                  validation.clearErrors(["employeesOnCOBRA", "useOptimylCOBRA"]);
                }
              }}
            />
          </>
        ) : (
          <Alert
            type="info"
            showIcon={false}
            message="Federal COBRA applies and is required for employers with 20 or more employees. Your group qualifies based on the employee count provided."
            className="mb-4"
          />
        )}
        {showCOBRAFollowup && (
          <div className="space-y-5 section-reveal">
            <hr className="border-gray-200" />
            <YesNoInput
              field="employeesOnCOBRA"
              label="Employees currently on COBRA or in their COBRA election period?"
              validation={validation}
              required
            />
            <hr className="border-gray-200" />
            <YesNoInput
              field="useOptimylCOBRA"
              label="Use Optimyl's COBRA Administration Services?"
              validation={validation}
              required
              hint="Available at no additional cost. Requires Optimyl COBRA ACH."
            />
          </div>
        )}
      </div>

      {/* Section 125 */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold text-brand-navy mb-4 flex items-center gap-2">
          <FileText size={20} className="text-gray-500" />
          Section 125 Premium Only Plan
        </h2>
        <Alert
          type="info"
          showIcon={false}
          message="You may want this if you offer benefits on a pre-tax basis and do not currently have a Section 125 plan in place you will continue to use."
          className="mb-4"
        />
        <YesNoInput
          field="wantOptimylSection125"
          label="Would you like Optimyl to provide a Section 125 Plan Document?"
          validation={validation}
          required
          onChange={(v) => {
            if (v === "No") {
              setField("section125PlanMedical", false);
              setField("section125PlanDental", false);
              setField("section125PlanVision", false);
              setField("section125TierEmployeeOnly", false);
              setField("section125TierEmployeeSpouse", false);
              setField("section125TierEmployeeChildren", false);
              setField("section125TierEmployeeFamily", false);
              validation.clearErrors(["section125PlanMedical", "section125TierEmployeeOnly"]);
            }
          }}
        />
        {wantOptimylSection125 === "Yes" && (
          <div className="mt-4 space-y-4 section-reveal">
            <Alert
              type="info"
              showIcon={false}
              message="Please note that our Plan Document service is available if Optimyl Medical coverage is elected. The other benefit plans may be offered through a provider other than Optimyl and we will include them in the Plan Document for you."
            />
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Plans to include<span className="text-danger" aria-hidden="true">&nbsp;*</span>
              </p>
              <div className="flex flex-wrap gap-2">
                <CheckboxInput field="section125PlanMedical" label="Medical" variant="pill" />
                <CheckboxInput field="section125PlanDental" label="Dental" variant="pill" />
                <CheckboxInput field="section125PlanVision" label="Vision" variant="pill" />
              </div>
              {validation.getFieldError("section125PlanMedical") && (
                <p className="text-xs text-danger mt-1.5">{validation.getFieldError("section125PlanMedical")}</p>
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">
                Tiers offered on a pre-tax basis<span className="text-danger" aria-hidden="true">&nbsp;*</span>
              </p>
              <div className="flex flex-wrap gap-2">
                <CheckboxInput field="section125TierEmployeeOnly" label="Employee Only" variant="pill" />
                <CheckboxInput field="section125TierEmployeeSpouse" label="Employee & Spouse" variant="pill" />
                <CheckboxInput field="section125TierEmployeeChildren" label="Employee & Children" variant="pill" />
                <CheckboxInput field="section125TierEmployeeFamily" label="Employee & Family" variant="pill" />
              </div>
              {validation.getFieldError("section125TierEmployeeOnly") && (
                <p className="text-xs text-danger mt-1.5">{validation.getFieldError("section125TierEmployeeOnly")}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
